import {DosageUnit, FrequencyUnit} from './athena/models/Paperwork';

/* prettier-ignore */
export type Person = {
    id: string;
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    insertedAt: string; // Not yet parsed into a Date
};

export type Address = {
    Street: string;
    City: string;
    State: string;
    ZipCode: string;
    Country: string;
    DefaultZipCode: string;
    DefaultState: string;
};

export const EventType = {
    ADVISOR_CALL_EVENT: 'advisor_call',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type EventType = (typeof EventType)[keyof typeof EventType];

export type Event = {
    id: string;
    type: EventType;
    date: string;
    updatedAt: string;
};

export type Document = {
    id: string;
    s3key: string;
    name: string;
    filename: string;
    downloadUrl: string;
    uploadUrl: string;
};

export const relationshipMigrationMap: Record<string, string> = {
    spouse: 'husband',
    parent: 'mother',
    child: 'son',
    significant_other: 'fiance/fiancee',
    sibling: 'brother',
};

export const Relationship = {
    HUSBAND: 'husband',
    WIFE: 'wife',
    FIANCE_FIANCEE: 'fiance/fiancee',
    SON: 'son',
    DAUGHTER: 'daughter',
    SISTER: 'sister',
    BROTHER: 'brother',
    MOTHER: 'mother',
    FATHER: 'father',
    GRANDCHILD: 'grandchild',
    GRANDPARENT: 'grandparent',
    OTHER: 'other',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type Relationship = (typeof Relationship)[keyof typeof Relationship];

export const RelationshipHumanizedMap = {
    [Relationship.HUSBAND]: 'Husband',
    [Relationship.WIFE]: 'Wife',
    [Relationship.FIANCE_FIANCEE]: 'Fiance/Fiancee',
    [Relationship.SON]: 'Son',
    [Relationship.DAUGHTER]: 'Daughter',
    [Relationship.SISTER]: 'Sister',
    [Relationship.BROTHER]: 'Brother',
    [Relationship.MOTHER]: 'Mother',
    [Relationship.FATHER]: 'Father',
    [Relationship.GRANDCHILD]: 'Grandchild',
    [Relationship.GRANDPARENT]: 'Grandparent',
    [Relationship.OTHER]: 'Other',
} as const;
export type Beneficiary = Person & {
    relationship: Relationship;
    relationshipNote: string;
    payoutPercentage: number;
    contingent: boolean;
    isFinanciallyDepend?: boolean;
};

export type Medication = {
    name: string;
    dosageQuantity: number | null;
    dosageUnit: DosageUnit | null;
    frequency: FrequencyUnit | null;
    lastDoseDate: string | null;
};

export type Condition = {
    name: string;
    medications: Medication[];
};

export type RecentlyRemoved = {
    condition: Condition | null;
    medication: Medication | null;
};

export type AdditionalConditions = {
    containsAdditionalConditions: boolean;
    conditions: AdditionalCondition[];
};

export type AdditionalCondition = {
    name: string;
    medications: Medication[];
};

export const FormSpecLabels = {
    ADDRESS: 'address',
    BENEFICIARIES: 'beneficiaries',
    ADDITIONAL_HEALTH: 'additionalHealth',
    EMPLOYMENT: 'employment',
    ADDITIONAL_CONDITIONS: 'additionalConditions',
    ADDITIONAL_POLICIES: 'additionalPolicyInterests',
    BIRTH_STATUS: 'birthStatus',
    FINANCIAL_SUITABILITY: 'financialSuitability',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type FormSpecLabels = (typeof FormSpecLabels)[keyof typeof FormSpecLabels];

export type FormSpec = {
    id: string;
    label: FormSpecLabels;
};

export type Paperwork<S = string, C = any> = {
    id: string;
    label: string;
    status: S;
    completedPaperwork: C;
    formSpec: FormSpec;
    formSpecId: string;
    insertedAt: string; // Not converted to Date yet
    updatedAt: string; // Not converted to Date yet
};

export const BeneficiariesPaperworkStatus = {
    INCOMPLETE: 'incomplete',
    CONFIRMED: 'confirmed',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type BeneficiariesPaperworkStatus =
    (typeof BeneficiariesPaperworkStatus)[keyof typeof BeneficiariesPaperworkStatus];

export type BeneficiariesPaperwork = Paperwork<BeneficiariesPaperworkStatus>;

export const AdditionalConditionsPaperworkStatus = {
    INCOMPLETE: 'incomplete',
    CONFIRMED: 'confirmed',
} as const;

export const FinancialSuitabilityPaperworkStatus = {
    INCOMPLETE: 'incomplete',
    CONFIRMED: 'confirmed',
} as const;

export type FinancialSuitabilityPaperworkStatus =
    (typeof FinancialSuitabilityPaperworkStatus)[keyof typeof FinancialSuitabilityPaperworkStatus];

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type AdditionalConditionsPaperworkStatus =
    (typeof AdditionalConditionsPaperworkStatus)[keyof typeof AdditionalConditionsPaperworkStatus];

export type AdditionalConditionsPaperwork = Paperwork<AdditionalConditionsPaperworkStatus>;

export const AdditionalPolicyInterestPaperworkStatus = {
    SKIPPED: 'skipped',
    SUBMITTED: 'submitted',
} as const;

type AdditionalPolicyInterestPaperworkValue = {
    additionalPolicyInterests: Array<string>;
};
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type AdditionalPolicyInterestPaperworkStatus =
    (typeof AdditionalPolicyInterestPaperworkStatus)[keyof typeof AdditionalPolicyInterestPaperworkStatus];

export type AdditionalPolicyInterestPaperwork = Paperwork<
    AdditionalPolicyInterestPaperworkStatus,
    AdditionalPolicyInterestPaperworkValue
>;

export type AddHealth = {
    weightChanged: string;
    pcpName: string;
    pcpStreet?: string;
    pcpState?: string;
    pcpCity?: string;
    pcpZip?: string;
    pcpLastVisited?: string;
    pcpPhone?: string;
    fatherStillLiving: string;
    fatherAge?: string;
    fatherPassedAtAge?: string;
    fatherCauseOfPassing?: string;
    fatherAdditionalDetails?: string;
    motherStillLiving: string;
    motherAge?: string;
    motherPassedAtAge?: string;
    motherCauseOfPassing?: string;
    motherAdditionalDetails?: string;
    showPCP?: string;
    pcpVisitReason?: string;
    pcpVisitOutcome?: string;
    pcpVisitDate?: string;
    pcpVisitProviderName?: string;
    pcpVisitStreet?: string;
    pcpVisitState?: string;
};

export const AddHealthKeys = {
    WEIGHT_CHANGED: 'weightChanged',
    PCP_NAME: 'pcpName',
    PCP_FIRST_NAME: 'pcpFirstName',
    PCP_LAST_NAME: 'pcpLastName',
    PCP_STREET: 'pcpStreet',
    PCP_STATE: 'pcpState',
    PCP_CITY: 'pcpCity',
    PCP_ZIP: 'pcpZip',
    PCP_LAST_VISITED: 'pcpLastVisited',
    PCP_PHONE: 'pcpPhone',
    PCP_VISIT_REASON: 'pcpVisitReason',
    PCP_VISIT_OUTCOME: 'pcpVisitOutcome',
    PCP_VISIT_FOLLOW_UP_ADDN_DETAILS: 'pcpVisitFollowUpAddnDetails',
    LAST_DOCTOR_VISIT_PROVIDER_NAME: 'lastDoctorVisitProviderName',
    LAST_DOCTOR_VISIT_STREET: 'lastDoctorVisitStreet',
    LAST_DOCTOR_VISIT_STATE: 'lastDoctorVisitState',
    LAST_DOCTOR_VISIT_CITY: 'lastDoctorVisitCity',
    LAST_DOCTOR_VISIT_ZIP: 'lastDoctorVisitZip',
    LAST_DOCTOR_VISIT_LAST_VISITED: 'lastDoctorVisitLastVisited',
    LAST_DOCTOR_VISIT_IS_REGULAR_DOCTOR: 'lastDoctorVisitIsRegularDoctor',
    FATHER_STILL_LIVING: 'fatherStillLiving',
    FATHER_AGE: 'fatherAge',
    FATHER_PASSED_AT_AGE: 'fatherPassedAtAge',
    FATHER_CAUSE_OF_PASSING: 'fatherCauseOfPassing',
    FATHER_ADDITIONAL_DETAILS: 'fatherAdditionalDetails',
    MOTHER_STILL_LIVING: 'motherStillLiving',
    MOTHER_AGE: 'motherAge',
    MOTHER_PASSED_AT_AGE: 'motherPassedAtAge',
    MOTHER_CAUSE_OF_PASSING: 'motherCauseOfPassing',
    MOTHER_ADDITIONAL_DETAILS: 'motherAdditionalDetails',
    SHOW_PCP: 'showPCP',
    EXISTING_COVERAGE: 'existingCoverage',
    COVERAGE_PROVIDER_NAME: 'coverageProviderName',
    COVERAGE_PROVIDER_NAME1: 'coverageProviderName1',
    COVERAGE_PROVIDER_NAME2: 'coverageProviderName2',
    COVERAGE_PROVIDER_NAME_UNLISTED: 'coverageProviderNameUnlisted',
    COVERAGE_PROVIDER_NAME_UNLISTED1: 'coverageProviderNameUnlisted1',
    COVERAGE_PROVIDER_NAME_UNLISTED2: 'coverageProviderNameUnlisted2',
    EXISTING_COVERAGE_AMOUNT: 'existingCoverageAmount',
    EXISTING_COVERAGE_AMOUNT1: 'existingCoverageAmount1',
    EXISTING_COVERAGE_AMOUNT2: 'existingCoverageAmount2',
    EXISTING_POLICY_TYPE: 'existingPolicyType',
    EXISTING_POLICY_TYPE1: 'existingPolicyType1',
    EXISTING_POLICY_TYPE2: 'existingPolicyType2',
    CONFIRM_EXISTING_COVERAGE_NOT_REPLACEMENT_OR_EXCHANGE: 'confirmExistingCoverageNotReplacementOrExchange',
    CONFIRM_EXISTING_COVERAGE_NOT_REPLACEMENT_OR_EXCHANGE1: 'confirmExistingCoverageNotReplacementOrExchange1',
    CONFIRM_EXISTING_COVERAGE_NOT_REPLACEMENT_OR_EXCHANGE2: 'confirmExistingCoverageNotReplacementOrExchange2',
    PAST_APPLICATION: 'pastApplication',
    PAST_APPLICATION_COVERAGE_AMOUNT: 'pastApplicationCoverageAmount',
    PAST_APPLICATION_POLICY_TYPE: 'pastApplicationPolicyType',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type AddHealthKeys = (typeof AddHealthKeys)[keyof typeof AddHealthKeys];

export const AddHealthFormControlKeys = {
    IS_NEXT_DISABLED: 'isNextDisabled',
    IS_SAVING: 'isSaving',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type AddHealthFormControlKeys = (typeof AddHealthFormControlKeys)[keyof typeof AddHealthFormControlKeys];

export const AddHealthActions = {
    UPDATE: 'UPDATE',
    UPDATEBULK: 'UPDATEBULK',
    IMPORT: 'IMPORT',
    CLEAREXISTINGCOVERAGE: 'CLEAREXISTINGCOVERAGE',
    CLEARPCP: 'CLEARPCP',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type AddHealthActions = (typeof AddHealthActions)[keyof typeof AddHealthActions];

export const AdditionalHealthPaperworkStatus = {
    INCOMPLETE: 'incomplete',
    CONFIRMED: 'confirmed',
} as const;

export const PaperworkStatus = {
    CONFIRMED: 'confirmed',
};
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type AdditionalHealthPaperworkStatus =
    (typeof AdditionalHealthPaperworkStatus)[keyof typeof AdditionalHealthPaperworkStatus];

export type AdditionalHealthPaperwork = Paperwork<AdditionalHealthPaperworkStatus>;

export const PaymentFrequency = {
    MONTHLY: 'Monthly',
    QUARTERLY: 'Quarterly',
    EVERY_SIX_MONTHS: 'Every 6 Months',
    ONCE_PER_YEAR: 'Once Per Year',
} as const;

export const PaymentPreferredDraftDate = {
    FIRST_OF_MONTH: 'First of the month',
    FIFTEENTH_OF_MONTH: 'Fifteenth of the month',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type PaymentFrequency = (typeof PaymentFrequency)[keyof typeof PaymentFrequency];

export type PaymentInformation = {
    id: string;
    label: string;
    customerId: string;
    accountHoldersName: string;
    bankName: string;
    accountNumber: string;
    routingNumber: string;
    accountType: 'checking' | 'savings';
    paymentFrequency: PaymentFrequency;
    dayOfMonth: number;
    ownerName: string;
    institutionName: string;
    plaidAccountId: string;
    insertedAt: string;
    updatedAt: string;
};

export const FirstIdentificationType = {
    US_CITIZEN: 'us_citizen',
    PERMANENT_RESIDENT_NUMBER: 'permanent_resident_number',
    US_VISA: 'us_visa',
    TAXPAYER_IDENTIFICATION_NUMBER: 'taxpayer_identification_number',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type FirstIdentificationType = (typeof FirstIdentificationType)[keyof typeof FirstIdentificationType];

export const SecondIdentificationType = {
    DRIVERS_LICENSE: 'drivers_license',
    STATE_ID: 'state_issued_id',
    NO_ID: 'no_id',
    NO_ID_MINOR: 'no_id_minor',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type SecondIdentificationType = (typeof SecondIdentificationType)[keyof typeof SecondIdentificationType];

// Misc identification type that aren't used for first or second
export const MiscIdentificationType = {
    SOCIAL_SECURITY_NUMBER: 'social_security_number',
};

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type MiscIdentificationType = (typeof MiscIdentificationType)[keyof typeof MiscIdentificationType];

export const ReasonsNoDriversLicense = {
    NEVER_OBTAINED_LICENSE: 'never-obtained-a-drivers-license',
    LAPSED_LICENSE: 'license-has-lapsed',
    SUSPENDED_LICENSE: 'license-has-been-suspended',
} as const;

export type ReasonsNoDriversLicenseType = (typeof ReasonsNoDriversLicense)[keyof typeof ReasonsNoDriversLicense];

export const IdentificationType = {
    ...FirstIdentificationType,
    ...SecondIdentificationType,
    ...MiscIdentificationType,
} as const;

export type IdentificationType = (typeof IdentificationType)[keyof typeof IdentificationType];

export type Identification<Type extends IdentificationType = IdentificationType, Value = any> = {
    id: string;
    type: Type;
    value: Value;
    insertedAt: string;
    updatedAt: string;
};

export type FirstIdentificationValue = {
    socialSecurityNumber?: string;
    permanentResidentNumber?: string;
    permanentResidentExpirationDate?: string;
    usVisaType?: string;
    usVisaNumber?: string;
    usTinNumber?: string;
};

export type FirstIdentification = Identification<FirstIdentificationType, FirstIdentificationValue>;

export type SecondIdentificationValue = {
    state?: string;
    number?: string;
    exp?: string;
    reason?: string;
    reasonNoLicense?: string;
    modeOfTransport?: string;
};

export type SecondIdentification = Identification<SecondIdentificationType, SecondIdentificationValue>;

export const RequirementType = {
    CONFIRM_SIGNATURE: 'confirmSignature',
    DOCUSIGN: 'docusign',
    EMPLOYMENT_INFO: 'employmentInfo',
    GENERIC_RESPONSE: 'genericResponse',
    GENERIC_UPLOAD: 'genericUpload',
    PRIMARY_CARE_PROVIDER: 'primaryCareProvider',
    PAYMENT_INFORMATION: 'paymentInformation',
    FAMILY_HISTORY: 'familyHistory',
    FAMILY_HISTORY_FATHER: 'familyHistoryFather',
    FAMILY_HISTORY_MOTHER: 'familyHistoryMother',
    MEDICAL_VISIT_RESPONSE: 'MedicalVisitResponse',
    PARAMEDICAL_EXAM_AVAILABILITY_RESPONSE: 'ParamedicalExamAvailabilityResponse',
    PRESCRIPTION_DETAILS_RESPONSE: 'PrescriptionDetailsResponse',
    LAST_SUBSTANCE_USAGE_RESPONSE: 'LastSubstanceUsageResponse',
    TOBACCO_USE_DETAILS_RESPONSE: 'TobaccoUseDetailsResponse',
    BANKRUPTCY_DETAILS_RESPONSE: 'BankruptcyDetailsResponse',
    OTHER_INSURANCE_ACTIVITY_RESPONSE: 'OtherInsuranceActivityResponse',
    SOURCE_OF_INCOME_RESPONSE: 'SourceOfIncomeResponse',
    PRIOR_EMPLOYMENT_DETAILS_RESPONSE: 'PriorEmploymentDetailsResponse',
    CRIMINAL_HISTORY_DETAILS_RESPONSE: 'CriminalHistoryDetailsResponse',
    BLOOD_GLUCOSE_READING_RESPONSE: 'BloodGlucoseReadingResponse',
    VERIFY_HEIGHT_WEIGHT_RESPONSE: 'VerifyHeightWeightResponse',
    COVID_TESTING_DETAILS_RESPONSE: 'CovidTestingDetailsResponse',
    PHOTO_ID_UPLOAD: 'photoIdUpload',
    MEDICAL_RESULTS_UPLOAD: 'MedicalResultsUpload',
    PAYMENT_AUTHORIZATION_DOCUSIGN: 'paymentAuthorizationDocusign',
    POLICY_DOCUSIGN: 'policyDocusign',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type RequirementType = (typeof RequirementType)[keyof typeof RequirementType];

export const RequirementStatus = {
    OUTSTANDING: 'outstanding',
    ATTENTION: 'attention',
    RECEIVED: 'received',
    UNDER_REVIEW: 'under-review',
    SCHEDULED: 'scheduled',
    COMPLETED: 'completed',
    SUBMITTED: 'submitted',
    REJECTED: 'rejected',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type RequirementStatus = (typeof RequirementStatus)[keyof typeof RequirementStatus];

export type Requirement<DataJson = any> = {
    id: string;
    type: RequirementType;
    status: RequirementStatus;
    title: string;
    link?: string;
    additionalInstructions?: string;
    internalNote?: string;
    fileTitle?: string;
    data?: string;
    dataJson?: DataJson;
    data_json?: DataJson;
    customerId: string;
    paymentInformationId?: string;
    policyId?: string;
    customer?: Customer;
    documents?: Document[];
    insertedAt: string;
    updatedAt: string;
    private: boolean;
};

export type Policy = {
    id: string;
    status: 'pending_update' | 'active';
    carrierName: string;
    number: string;
    label: string;
    coverageAmount: string;
    monthlyContribution: string;
    customerId: string;
    customer: Customer;
    documents: Document[];
    requirements: Requirement[];
};

export type Customer = Person & {
    additionalPoliciesComplete: boolean;
    email?: string;
    address: Partial<Address>;
    consent?: string;
    phoneNumber?: string;
    paperworks: Paperwork[];
    applicationSubmittedAt?: string;
    advisorCallEvent?: Event | null;
    identifications: Identification[];
    firstIdentification?: FirstIdentification | null;
    secondIdentification?: SecondIdentification | null;
    paymentInformation?: PaymentInformation | null;
    paymentInformations: PaymentInformation[];
    policies: Policy[];
    events: Event[];
    requirements: Requirement[];
    underwritingRequirements: Requirement[];
    verificationComplete: boolean;
    underwritingComplete: boolean;
    employmentVerified: boolean;
    bankingComplete: boolean;
    paymentAuthorized: boolean;
    additionalHealthConfirmed: boolean;
    beneficiariesConfirmed: boolean;
    medicalWrapUpConfirmed: boolean;
    walkthrough: any;
    walkthroughComplete: boolean;
    canSubmitApplication: boolean;
    privateRequirements: number;
    birthStatus: BirthStatusType;
    applicationQuestionsComplete: boolean;
    financialSuitabilityConfirmed: boolean;
    quote: any;
    policyData: any[];
};

export type User = Customer;

export type UserBlob = Omit<
    User,
    | 'additionalPoliciesComplete'
    | 'firstIdentification'
    | 'secondIdentification'
    | 'paymentInformation'
    | 'advisorCallEvent'
    | 'address'
    | 'verificationComplete'
    | 'underwritingRequirements'
    | 'underwritingComplete'
    | 'employmentVerified'
    | 'bankingComplete'
    | 'paymentAuthorized'
    | 'additionalHealthConfirmed'
    | 'beneficiariesConfirmed'
    | 'canSubmitApplication'
    | 'privateRequirements'
    | 'birthStatus'
    | 'reviewScreenSummary'
>;

export interface EmploymentInfoInterface {
    id?: string;
    isSelfEmployed?: boolean;
    employerName?: string;
    employmentStartDate?: string;
    occupation?: string;
    industryDescription?: string;
    prevIsSelfEmployed?: boolean;
    prevEmployerName?: string;
    prevEmploymentStartDate?: string;
    prevOccupation?: string;
    prevIndustryDescription?: string;
    reflectiveQuestionHomeMakerHouseholdIncomeSource?: string;
    reflectiveQuestionHomeMakerHouseholdIncome?: string;
    reflectiveQuestionHomeMakerNetWorth?: string;
    reflectiveQuestionHomeMakerSpouseHasSimilarCoverage?: string;
    reflectiveQuestionOnDisabilityDetails?: string;
    reflectiveQuestionOnDisabilityReceivingSSDI?: string;
    reflectiveQuestionRetiredHaveEarnedAnnualIncome?: string;
    reflectiveQuestionRetiredNetWorth?: string;
    reflectiveQuestionRetiredNonEMCDetailsCurrentIncome?: string;
    reflectiveQuestionRetiredNonEMCAgeAtRetirement?: string;
    reflectiveQuestionStudentSourceOfIncome?: string;
    reflectiveQuestionStudentParentsSimilarAmountsOfLifeInsuranceApplyingForSimilarAmounts?: string;
    reflectiveQuestionStudentSpousePartnerSimilarAmountsOfLifeInsuranceApplyingForSimilarAmounts?: string;
    reflectiveQuestionStudentNonEMCSchoolName?: string;
    reflectiveQuestionStudentNonEMCGrade?: string;
    reflectiveQuestionUnemployedReason?: string;
    reflectiveQuestionUnemployedPreviousEmployer?: string;
    reflectiveQuestionUnemployedRole?: string;
    reflectiveQuestionUnemployedYearsEmployed?: string;
    reflectiveQuestionOtherOccupationDetails?: string;
}

export interface BirthStatusType {
    birthState?: string;
    birthCountry?: string;
}
